import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  FileText, 
  DollarSign,
  Calendar,
  Eye,
  Award,
  Key
} from 'lucide-react';
import { VendedorMetrics } from '@/types/vendedores';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

// Função de formatação de telefone
const formatTelefone = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  } else if (digits.length === 10) {
    return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return value;
};

interface VendedorCardProps {
  vendedor: VendedorMetrics;
  onClick: () => void;
}

export function VendedorCard({ vendedor, onClick }: VendedorCardProps) {
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const getPerformanceColor = (taxa: number) => {
    if (taxa >= 20) return 'text-green-600 bg-green-50';
    if (taxa >= 10) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getPerformanceIcon = (taxa: number) => {
    if (taxa >= 10) return <TrendingUp className="h-4 w-4" />;
    return <TrendingDown className="h-4 w-4" />;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Função para extrair iniciais do nome (primeira letra do primeiro nome + primeira letra do último sobrenome)
  const getInitials = (nome: string) => {
    if (!nome) return 'U';
    
    const partes = nome.trim().split(/\s+/).filter(p => p.length > 0);
    
    if (partes.length === 0) return 'U';
    if (partes.length === 1) return partes[0][0].toUpperCase();
    
    // Primeira letra do primeiro nome + primeira letra do último sobrenome
    const primeira = partes[0][0].toUpperCase();
    const ultima = partes[partes.length - 1][0].toUpperCase();
    
    return primeira + ultima;
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Evita que o clique no card seja acionado
    
    // Gerar link de ativação para desenvolvimento
    const linkAtivacao = `/vendedor/definir-senha?token_hash=${vendedor.vendedor_id}&type=invite`;
    
    // Mostrar toast com o link
    toast({
      title: "🔗 Link de Ativação Gerado",
      description: (
        <div className="space-y-2">
          <p className="text-sm">Clique no link abaixo para ativar a conta do vendedor:</p>
          <a 
            href={linkAtivacao}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline text-sm break-all"
          >
            {window.location.origin}{linkAtivacao}
          </a>
          <p className="text-xs text-gray-500">
            ⚠️ Modo desenvolvimento - em produção o link será enviado por e-mail
          </p>
        </div>
      ),
      duration: 10000, // 10 segundos para dar tempo de copiar
    });
  };

  return (
    <Card className="hover:shadow-lg transition-shadow cursor-pointer hover:border-primary/50">
      <CardHeader className="pb-3 px-4 sm:px-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
            <Avatar className="h-8 w-8 sm:h-12 sm:w-12 border-2 border-primary/20 flex-shrink-0">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs sm:text-sm">
                {getInitials(vendedor.vendedor_nome)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm sm:text-base truncate">{vendedor.vendedor_nome}</h3>
              <p className="text-xs text-muted-foreground truncate">{vendedor.vendedor_email}</p>
              {vendedor.vendedor_telefone && (
                <p className="text-xs text-muted-foreground truncate">{formatTelefone(vendedor.vendedor_telefone)}</p>
              )}
            </div>
          </div>
          {vendedor.status ? (
            <Badge variant="default" className="flex-shrink-0 text-xs">
              Ativo
            </Badge>
          ) : (
            <Badge 
              variant="secondary" 
              className="cursor-pointer hover:bg-yellow-100 hover:text-yellow-800 transition-colors flex items-center gap-1 flex-shrink-0 text-xs"
              onClick={handleStatusClick}
              title="Clique para gerar link de ativação"
            >
              <Key className="h-3 w-3" />
              {!isMobile && 'Inativo'}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6">
        {/* Data de Início */}
        <div className="flex items-center text-xs text-muted-foreground">
          <Calendar className="h-3 w-3 mr-1" />
          Desde {new Date(vendedor.data_inicio).toLocaleDateString('pt-BR')}
        </div>

        {/* Métricas de Parceiros */}
        <div className="bg-blue-50 rounded-lg p-2 sm:p-3 border border-blue-100">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-blue-900">Rede de Parceiros</span>
            <Users className="h-4 w-4 text-blue-600 flex-shrink-0" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-xl sm:text-2xl font-bold text-blue-600">{vendedor.total_parceiros}</span>
            <span className="text-xs text-blue-600">
              {vendedor.parceiros_ativos} ativos
            </span>
          </div>
          {vendedor.parceiros_30_dias > 0 && (
            <p className="text-xs text-green-600 mt-1">
              +{vendedor.parceiros_30_dias} este mês
            </p>
          )}
        </div>

        {/* Métricas Principais Grid 2x2 */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <div className="text-center p-2 sm:p-2.5 bg-muted/50 rounded-lg">
            <div className="text-base sm:text-lg font-bold text-blue-600">{vendedor.total_leads}</div>
            <div className="text-xs text-muted-foreground">Leads</div>
            {vendedor.leads_30_dias > 0 && (
              <div className="text-xs text-green-600">+{vendedor.leads_30_dias}</div>
            )}
          </div>
          
          <div className="text-center p-2 sm:p-2.5 bg-muted/50 rounded-lg">
            <div className="text-base sm:text-lg font-bold text-green-600">{vendedor.propostas_enviadas}</div>
            <div className="text-xs text-muted-foreground">Propostas</div>
            {vendedor.propostas_30_dias > 0 && (
              <div className="text-xs text-green-600">+{vendedor.propostas_30_dias}</div>
            )}
          </div>
          
          <div className="text-center p-2 sm:p-2.5 bg-muted/50 rounded-lg">
            <div className="text-base sm:text-lg font-bold text-purple-600">{vendedor.contratos_fechados}</div>
            <div className="text-xs text-muted-foreground">Contratos</div>
            {vendedor.contratos_30_dias > 0 && (
              <div className="text-xs text-green-600">+{vendedor.contratos_30_dias}</div>
            )}
          </div>
          
          <div className="text-center p-2 sm:p-2.5 bg-muted/50 rounded-lg">
            <div className="text-xs sm:text-sm font-bold text-orange-600 break-words">
              {formatCurrency(vendedor.valor_contratos)}
            </div>
            <div className="text-xs text-muted-foreground">Valor Total</div>
          </div>
        </div>

        {/* Melhor Parceiro */}
        {vendedor.melhor_parceiro_nome && (
          <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Award className="h-4 w-4 text-yellow-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-yellow-900">Parceiro Destaque</p>
              <p className="text-xs text-yellow-700 truncate">{vendedor.melhor_parceiro_nome}</p>
            </div>
            <Badge variant="secondary" className="text-xs flex-shrink-0">
              {vendedor.melhor_parceiro_contratos} contratos
            </Badge>
          </div>
        )}

        {/* Taxa de Conversão */}
        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-xs text-muted-foreground">Taxa de Conversão</span>
          <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getPerformanceColor(vendedor.taxa_conversao_leads_contratos)}`}>
            {getPerformanceIcon(vendedor.taxa_conversao_leads_contratos)}
            <span>{vendedor.taxa_conversao_leads_contratos}%</span>
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end pt-2">
          <Button size="sm" variant="outline" onClick={onClick} className="w-full">
            <Eye className="h-3 w-3 mr-2" />
            Ver Detalhes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

